"use client"

import styled from "@emotion/styled"
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { colors, mediaQueries } from "ui/theme/theme"
import ButtonWithArrow from "../../common/bit/ButtonWithArrow"

const StyledContainer = styled(Box)`
  width: 100%;
`

const StyledDetailsContainer = styled(Box)`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledDetailRow = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${mediaQueries.desktop} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const StyledLabel = styled(Typography)`
  font-weight: 700;
  text-align: start;
  flex: 1;
`

const StyledCheckboxLabel = styled(Typography)`
  color: ${colors.black};
  font-weight: 700;
`

const StyledValue = styled(Typography)`
  font-weight: 700;
  text-align: start;
  color: ${colors.black};
  flex: 1;
`

const StyledTitle = styled(Typography)`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 24px;
  color: ${colors.black};
`

const StyledCheckboxGroup = styled(Box)`
  display: flex;
  flex-direction: row;
  flex: 1;

  .MuiFormControlLabel-root {
    margin-right: 32px;
  }
`

const StyledCheckbox = styled(Checkbox)`
  padding: 4px;
  &.Mui-checked {
    color: ${colors.red};
  }
`

const StyledButtonContainer = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`
const StyledTypographyWithBullet = styled(Typography)<{
  component?: React.ElementType
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 100%;
    content: "";
    margin-right: 8px;
    width: 22px;
    height: 22px;
    background-image: url("/icons/check.svg");
    object-fit: cover;
  }
`

interface CarInsuranceDetailsProps {
  data: any
  carDetails: {
    insuranceType: string
    agentName: string
    phoneNumber: string
    openClaims: string
  }
  onSubmit: (isRepaired: boolean) => void
  disabled?: boolean
}

const CarInsuranceDetails = ({
  data,
  carDetails,
  onSubmit,
  disabled = false,
}: CarInsuranceDetailsProps) => {
  const t = useTranslations("ThirdPartyCoverage")
  const [isRepaired, setIsRepaired] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Extract field values from data
  const {
    carDetailsTitle,
    insuranceTypeTitle,
    agentTitle,
    phoneTitle,
    claimsTitle,
    checkboxTitle,
    reportButtonText,
  } = {
    carDetailsTitle: data.carDetailsTitle?.value || "",
    insuranceTypeTitle: data.insuranceTypeTitle?.value || "",
    agentTitle: data.agentTitle?.value || "",
    phoneTitle: data.phoneTitle?.value || "",
    claimsTitle: data.claimsTitle?.value || "",
    checkboxTitle: data.checkboxTitle?.value || "",
    reportButtonText: data.reportButtonText?.value || "",
  }

  // Extract car details
  const { insuranceType, agentName, phoneNumber, openClaims } = carDetails

  const handleCheckboxChange = (value: boolean) => {
    setIsRepaired(value)
    setError(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (isRepaired === null) {
      setError("יש לבחור אחת מהאפשרויות")
      return
    }

    setError(null)
    onSubmit(isRepaired)
  }

  return (
    <StyledContainer>
      {carDetailsTitle && <StyledTitle>{carDetailsTitle}</StyledTitle>}

      <form onSubmit={handleSubmit}>
        <StyledDetailsContainer>
          {insuranceTypeTitle && insuranceType && (
            <StyledDetailRow>
              <StyledLabel>{insuranceTypeTitle}</StyledLabel>
              <StyledValue>{insuranceType}</StyledValue>
            </StyledDetailRow>
          )}

          {agentTitle && agentName && (
            <StyledDetailRow>
              <StyledLabel>{agentTitle}</StyledLabel>
              <StyledValue>{agentName}</StyledValue>
            </StyledDetailRow>
          )}

          <StyledDetailRow>
            {phoneTitle && <StyledLabel>{phoneTitle}</StyledLabel>}
            <StyledValue>{`3453* ${phoneNumber ? `או ${phoneNumber}` : ""}`}</StyledValue>
          </StyledDetailRow>

          {claimsTitle && openClaims && (
            <StyledDetailRow>
              <StyledLabel>{claimsTitle}</StyledLabel>
              <StyledValue>{openClaims}</StyledValue>
            </StyledDetailRow>
          )}

          {checkboxTitle && (
            <StyledDetailRow>
              <StyledLabel>{checkboxTitle}</StyledLabel>

              <StyledCheckboxGroup>
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={isRepaired === true}
                      onChange={() => handleCheckboxChange(true)}
                      disabled={disabled}
                    />
                  }
                  label={
                    <StyledCheckboxLabel>{t("yesOption")}</StyledCheckboxLabel>
                  }
                  disabled={disabled}
                />
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={isRepaired === false}
                      onChange={() => handleCheckboxChange(false)}
                      disabled={disabled}
                    />
                  }
                  label={
                    <StyledCheckboxLabel>{t("noOption")}</StyledCheckboxLabel>
                  }
                  disabled={disabled}
                />
              </StyledCheckboxGroup>
              {error && (
                <Typography color="error" variant="caption" display="block">
                  {error}
                </Typography>
              )}
            </StyledDetailRow>
          )}
        </StyledDetailsContainer>
        {isRepaired !== false ? (
          <>
            {!!isRepaired && (
              <StyledTypographyWithBullet variant="h5" component="p">
                {t("happyDayLabel")}
              </StyledTypographyWithBullet>
            )}
          </>
        ) : (
          <>
            {reportButtonText && (
              <StyledButtonContainer>
                <ButtonWithArrow
                  type="submit"
                  colorPallette="red"
                  disabled={disabled || isRepaired !== false}
                >
                  {reportButtonText}
                </ButtonWithArrow>
              </StyledButtonContainer>
            )}
          </>
        )}
      </form>
    </StyledContainer>
  )
}

export default CarInsuranceDetails
